import { Button, Media } from '../../components/ui'
import { BranchCard, BusinessHours, SectionHeading, directionsUrl } from '../../features/catalog'
import { useTenant } from '../../tenant/useTenant'

/**
 * E3 — where to find the salon and when it is open. The branch list comes from
 * bootstrap, which already carries every branch with its business hours, so the
 * home page costs no extra request.
 */
export function HomeLocations() {
  const { slug, bootstrap } = useTenant()
  const { vendor, branches } = bootstrap
  if (!branches.length) return null

  const multi = branches.length > 1
  const primary = branches[0]
  const address = [primary.address, primary.city].filter(Boolean).join(', ')
  const maps = directionsUrl(primary)

  if (multi) {
    return (
      <section className="py-section-gap-md md:py-section-gap-lg">
        <div className="container-page">
          <SectionHeading
            eyebrow="Visit"
            title="Locations"
            action={
              <Button to={`/${slug}/branches`} variant="text">
                All locations
              </Button>
            }
          />
          <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
            {branches.slice(0, 3).map((branch) => (
              <BranchCard key={branch.id} branch={branch} showHours />
            ))}
          </div>
        </div>
      </section>
    )
  }

  // Single branch: Stitch's "Location & Hours" split — the written details and
  // the week on the left, one large image panel on the right.
  return (
    <section className="py-section-gap-md md:py-section-gap-lg">
      <div className="container-page">
        <div className="grid grid-cols-1 items-start gap-16 lg:grid-cols-2">
          <div>
            <h2 className="mb-8 font-display text-headline-md text-on-surface">Location &amp; hours</h2>

            <div className="mb-8 flex flex-col gap-12 md:flex-row">
              <div>
                <h3 className="mb-4 font-label-caps text-label-caps uppercase text-primary">Visit us</h3>
                <p className="mb-1 font-body-md text-body-md text-on-surface-variant">{primary.name}</p>
                {address && <p className="mb-4 font-body-md text-body-md text-on-surface-variant">{address}</p>}
                {(primary.phone ?? vendor.phone) && (
                  <a
                    href={`tel:${primary.phone ?? vendor.phone}`}
                    className="block font-body-md text-body-md text-primary decoration-primary/50 hover:underline"
                  >
                    {primary.phone ?? vendor.phone}
                  </a>
                )}
                {vendor.email && (
                  <a
                    href={`mailto:${vendor.email}`}
                    className="block font-body-md text-body-md text-primary decoration-primary/50 hover:underline"
                  >
                    {vendor.email}
                  </a>
                )}
                {maps && (
                  <a
                    href={maps}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="mt-4 inline-flex items-center font-label-caps text-label-caps uppercase text-primary underline-offset-4 decoration-primary/50 transition-all hover:underline"
                  >
                    Get directions
                    <span className="material-symbols-outlined ml-2 text-[16px]">arrow_outward</span>
                  </a>
                )}
              </div>

              <div>
                <h3 className="mb-4 font-label-caps text-label-caps uppercase text-primary">Hours</h3>
                <BusinessHours hours={primary.business_hours} className="max-w-[12rem]" />
              </div>
            </div>

            <Button to={`/${slug}/book`} size="lg">
              Book now
            </Button>
          </div>

          <div className="relative h-[400px] w-full overflow-hidden rounded-lg border border-primary/10 bg-surface-container">
            <Media src={primary.image} alt={primary.name} kind="branch" />
          </div>
        </div>
      </div>
    </section>
  )
}
